
(() => {
  'use strict';

  const PRODUCT_SELECTOR = '[data-section-type="product"]';
  const OPTION_SELECTOR = '[name^="options["]';
  const POLL_MS = 200;
  const PLACEHOLDER = 'Selecionar tamanho';
  const BUTTON_PLACEHOLDER = 'SELECIONA O TEU TAMANHO';

  function getVariants(container) {
    const jsonEl = container.querySelector('[data-variant-json]');
    if (!jsonEl) return [];

    try {
      const variants = JSON.parse(jsonEl.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.warn('[Trotie] Variant JSON inválido.', error);
      return [];
    }
  }

  function getOptionGroups(container) {
    return [...container.querySelectorAll(OPTION_SELECTOR)]
      .filter((input) => !input.disabled);
  }

  function getSelectedInputs(container) {
    return getOptionGroups(container).filter((input) => {
      if (input.type === 'radio' || input.type === 'checkbox') {
        return input.checked;
      }

      return true;
    });
  }

  function isSizeInput(input) {
    const name = (input.name || '').toLowerCase();
    const wrapper = input.closest('.product__option');
    const text = (wrapper?.textContent || '').toLowerCase();

    return (
      name.includes('[size]') ||
      name.includes('[tamanho]') ||
      name.includes('[shoe size]') ||
      text.includes('tamanho:') ||
      text.includes('size:')
    );
  }

  function isColorInput(input) {
    const name = (input.name || '').toLowerCase();
    const wrapper = input.closest('.product__option');
    const text = (wrapper?.textContent || '').toLowerCase();

    return (
      name.includes('[cor]') ||
      name.includes('[color]') ||
      name.includes('[colour]') ||
      text.includes('cor:') ||
      text.includes('color:') ||
      text.includes('colour:')
    );
  }

  function getSizeGroup(container) {
    const input = getOptionGroups(container).find(isSizeInput);
    return input?.closest('.product__option') || null;
  }

  function getColorGroup(container) {
    const input = getOptionGroups(container).find(isColorInput);
    return input?.closest('.product__option') || null;
  }

  function getSelectedValues(container) {
    return getSelectedInputs(container).map((input) => input.value);
  }

  function getSelectedVariant(container) {
    const selected = getSelectedValues(container);

    if (
      selected.some((value) =>
        String(value).toLowerCase() === PLACEHOLDER.toLowerCase()
      )
    ) {
      return null;
    }

    return getVariants(container).find((variant) => {
      return (
        Array.isArray(variant.options) &&
        variant.options.length === selected.length &&
        variant.options.every(
          (value, index) => String(value) === String(selected[index])
        )
      );
    }) || null;
  }

  function updateOptionLabel(group, value) {
    if (!group) return;

    const label = group.querySelector(
      '[data-selected-value-for-option]'
    );

    if (label) {
      label.textContent = value;
    }
  }

  function ensureSizePlaceholder(container) {
    const group = getSizeGroup(container);
    if (!group) return;

    const select = group.querySelector('select[name^="options["]');

    if (select) {
      let placeholder = [...select.options].find(
        (option) => option.value === ''
      );

      if (!placeholder) {
        placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = PLACEHOLDER;
        select.insertBefore(placeholder, select.firstChild);
      }

      placeholder.textContent = PLACEHOLDER;
    }
  }

  function clearSize(container) {
    const group = getSizeGroup(container);
    if (!group) return;

    const select = group.querySelector('select[name^="options["]');

    if (select) {
      ensureSizePlaceholder(container);
      select.value = '';
      updateOptionLabel(group, PLACEHOLDER);
    }

    group
      .querySelectorAll('input[type="radio"][name^="options["]')
      .forEach((input) => {
        input.checked = false;
      });

    updateOptionLabel(group, PLACEHOLDER);

    container.dataset.trotieSizeRequired = 'true';
  }

  function syncVariantId(container, variant) {
    if (!variant) return;

    const variantId = String(variant.id);

    const variantSelect = container.querySelector(
      '[data-variant-select]'
    );

    if (variantSelect) {
      variantSelect.value = variantId;
    }

    container
      .querySelectorAll(
        'form[action*="/cart/add"] [name="id"], [data-product-form] [name="id"]'
      )
      .forEach((input) => {
        input.value = variantId;
      });

    const productForm = container.querySelector('[data-product-form]');

    if (productForm) {
      productForm.dataset.currentProductId = variantId;
    }

    container.dataset.currentProductId = variantId;

    container
      .querySelectorAll('[data-current-variant-id]')
      .forEach((element) => {
        element.dataset.currentVariantId = variantId;
      });
  }

  function updateUrl(variant) {
    if (!variant) return;

    const url = new URL(window.location.href);

    if (url.searchParams.get('variant') === String(variant.id)) {
      return;
    }

    url.searchParams.set('variant', String(variant.id));

    window.history.replaceState(
      { path: url.toString() },
      '',
      url.toString()
    );
  }

  function updateBuyButtons(container, variant) {
    const needsSize =
      container.dataset.trotieSizeRequired === 'true';

    container
      .querySelectorAll('[data-add-to-cart]')
      .forEach((button) => {
        const text = button.querySelector('[data-add-to-cart-text]');

        const availableText =
          button.dataset.langAvailable || 'Adicionar ao carrinho';

        const unavailableText =
          button.dataset.langUnavailable || 'Indisponível';

        const soldOutText =
          button.dataset.langSoldOut || 'Esgotado';

        if (needsSize) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-label', BUTTON_PLACEHOLDER);

          if (text) {
            text.textContent = BUTTON_PLACEHOLDER;
          }

          return;
        }

        if (!variant) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');

          if (text) {
            text.textContent = unavailableText;
          }

          return;
        }

        if (variant.available) {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.setAttribute('aria-label', availableText);

          if (text) {
            text.textContent = availableText;
          }
        } else {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-label', soldOutText);

          if (text) {
            text.textContent = soldOutText;
          }
        }
      });
  }

  function updateDynamicCheckout(container, variant) {
    const needsSize =
      container.dataset.trotieSizeRequired === 'true';

    container
      .querySelectorAll('.shopify-payment-button button')
      .forEach((button) => {
        button.disabled =
          needsSize || !variant || !variant.available;
      });
  }

  function handleCurrentSelection(container) {
    const needsSize =
      container.dataset.trotieSizeRequired === 'true';

    const variant = needsSize
      ? null
      : getSelectedVariant(container);

    if (!variant) {
      updateBuyButtons(container, null);
      updateDynamicCheckout(container, null);
      return;
    }

    syncVariantId(container, variant);
    updateUrl(variant);
    updateBuyButtons(container, variant);
    updateDynamicCheckout(container, variant);
  }

  function initProduct(container) {
    if (container.dataset.trotieVariantWatcher === 'true') {
      return;
    }

    container.dataset.trotieVariantWatcher = 'true';

    const sizeGroup = getSizeGroup(container);
    const colorGroup = getColorGroup(container);

    if (!sizeGroup) {
      handleCurrentSelection(container);
      return;
    }

    ensureSizePlaceholder(container);

    // TROTIE: exigir sempre uma escolha explícita do tamanho ao abrir
    // o produto, incluindo links de coleção com ?variant=...
    // A cor previamente selecionada mantém-se inalterada.
    clearSize(container);

    let lastColor = null;

    function readColor() {
      if (!colorGroup) return null;

      const select = colorGroup.querySelector(
        'select[name^="options["]'
      );

      if (select) return select.value;

      const checked = colorGroup.querySelector(
        'input[name^="options["]:checked'
      );

      return checked?.value || null;
    }

    lastColor = readColor();

    container.addEventListener('change', (event) => {
      const target = event.target;

      if (!(target instanceof HTMLElement)) return;

      if (!target.matches(OPTION_SELECTOR)) return;

      if (isColorInput(target)) {
        lastColor = readColor();
        clearSize(container);

        setTimeout(() => {
          clearSize(container);
          handleCurrentSelection(container);
        }, 0);
      } else if (isSizeInput(target)) {
        const value = target.value;

        container.dataset.trotieSizeRequired =
          value && value !== PLACEHOLDER
            ? 'false'
            : 'true';

        updateOptionLabel(
          sizeGroup,
          value || PLACEHOLDER
        );
      }

      setTimeout(() => {
        handleCurrentSelection(container);
      }, 0);
    });

    setInterval(() => {
      const currentColor = readColor();

      if (
        lastColor !== null &&
        currentColor !== null &&
        currentColor !== lastColor
      ) {
        lastColor = currentColor;
        clearSize(container);
      }

      if (
        container.dataset.trotieSizeRequired === 'true'
      ) {
        const select = sizeGroup.querySelector(
          'select[name^="options["]'
        );

        if (select && select.value !== '') {
          clearSize(container);
        }
      }

      handleCurrentSelection(container);
    }, POLL_MS);

    handleCurrentSelection(container);
  }

  function initAll() {
    document
      .querySelectorAll(PRODUCT_SELECTOR)
      .forEach(initProduct);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  window.addEventListener('load', initAll);

  initAll();

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      if (
        !form.matches(
          '[data-product-form], form[action*="/cart/add"]'
        )
      ) {
        return;
      }

      const container = form.closest(PRODUCT_SELECTOR);
      if (!container) return;

      if (
        container.dataset.trotieSizeRequired === 'true'
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        updateBuyButtons(container, null);
        return;
      }

      const variant = getSelectedVariant(container);

      if (!variant || !variant.available) {
        event.preventDefault();
        event.stopImmediatePropagation();
        updateBuyButtons(container, variant);
        return;
      }

      syncVariantId(container, variant);
    },
    true
  );
})();
